import type { VehicleType } from "@daytrip/legacy-enums";
import { DriversCompanyStatus, RejectionType } from "@daytrip/legacy-enums";
import { Exclude, Expose, Type } from "class-transformer";
import { IsArray, IsDate, IsDefined, IsString, Validate, IsNumber, IsBoolean } from "class-validator";

import { statusGetter } from "../statusGetter";
import { TransformToDate } from "../transformers/transformToDate";
import { IsIbanValidator } from "../validators/IsIbanValidator";

import { AcceptanceRateInfo } from "./AcceptanceRateInfo";
import { CooperationAgreement } from "./CooperationAgreement";
import { Document } from "./Document";
import { Position } from "./Position";
import { Score } from "./Score";

@Exclude()
export class DriversCompany {
    @IsString()
    @IsDefined()
    @Expose()
    public name: string;

    @IsString()
    @IsDefined()
    @Expose()
    public managementNote: string = "";

    @IsString()
    @IsDefined()
    @Expose()
    public town: string = "";

    @IsString()
    @IsDefined()
    @Expose()
    public address: string = "";

    @Expose()
    @Type(() => Position)
    public position?: Position;

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public originLocationIds: string[] = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Number)
    public willingToRentVehicleTypes: VehicleType[] = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Document)
    public documents: Document[] = [];

    @Validate(IsIbanValidator)
    @IsString()
    @Expose()
    public iban?: string;

    @IsDate()
    @Expose()
    @TransformToDate
    public hiredAt?: Date = new Date();

    @IsString()
    @Expose()
    public hireReason?: string;

    @IsDate()
    @Expose()
    @TransformToDate
    public activeAt?: Date;

    @IsDate()
    @Expose()
    @TransformToDate
    public declinedAt?: Date;

    @Expose()
    @Type(() => Score)
    public score: Score = new Score();

    @Expose()
    @Type(() => AcceptanceRateInfo)
    public acceptanceRateInfo: AcceptanceRateInfo = new AcceptanceRateInfo();

    @Expose()
    @Type(() => CooperationAgreement)
    public cooperationAgreement?: CooperationAgreement;

    @Expose()
    @IsBoolean()
    public hasCooperationAgreement?: boolean;

    public get status(): DriversCompanyStatus {
        return statusGetter(this, DriversCompany, DriversCompanyStatus);
    }

    public static statusPrototype(status: DriversCompanyStatus): DriversCompany {
        const defined = new Date();

        switch (status) {
            case DriversCompanyStatus.Declined:
                return {
                    declinedAt: defined,
                } as DriversCompany;

            case DriversCompanyStatus.Active:
                return {
                    activeAt: defined,
                    declinedAt: undefined,
                } as DriversCompany;

            case DriversCompanyStatus.Hired:
                return {
                    hiredAt: defined,
                    activeAt: undefined,
                    declinedAt: undefined,
                } as DriversCompany;
            default: {
                throw new Error(RejectionType.ServerError);
            }
        }
    }

    @IsString()
    @Expose()
    public hiredByUserId?: string;

    @IsNumber()
    @Expose()
    public acquisitionCost?: number;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public willingToDoLite: boolean = false;

    @IsString()
    @Expose()
    public apiKey?: string;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isNotDomesticVATPayer: boolean = false;
}
